import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

const HeroSection = ({ badge, title, subtitle }) => (
  <section className="px-6 pt-28 pb-16 md:pt-32 md:pb-24 max-w-7xl mx-auto" id="top">
    <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-16 items-center">
      <div className="space-y-8">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-4 py-1.5 text-xs font-medium text-emerald-300 ring-1 ring-emerald-400/20 mb-6">
            ✨ {badge || 'Trusted by Kenyan Chamas'}
          </div>
          <h1 className="text-5xl md:text-6xl font-bold leading-[1.1] text-[#f8f5ed] mb-6">
            {title || 'Stop chasing payments.\nStart growing together.'}
          </h1>
          <p className="text-xl text-[#d8d2c5]/90 leading-relaxed max-w-xl">
            {subtitle ||
              'GroupHela handles the busywork—contributions, loans, welfare, M-Pesa payments—so your chama can focus on what matters: supporting each other and building wealth.'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 rounded-full bg-emerald-400 text-[#0f1115] px-6 py-3.5 text-base font-semibold shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/40 hover:bg-emerald-300 transition-all"
          >
            Get started free
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </Link>
          <a
            href="#features"
            className="inline-flex items-center gap-2 text-base text-[#d8d2c5] hover:text-emerald-200 transition"
          >
            See how it works
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </a>
        </div>

        <div className="flex items-center gap-8 pt-6 border-t border-white/5">
          <div>
            <div className="text-2xl font-bold text-emerald-300">24/7</div>
            <div className="text-sm text-[#d8d2c5]/70">M-Pesa sync</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-cyan-300">100%</div>
            <div className="text-sm text-[#d8d2c5]/70">Audit ready</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-amber-300">&lt; 5min</div>
            <div className="text-sm text-[#d8d2c5]/70">Setup time</div>
          </div>
        </div>
      </div>

      <div className="relative">
        <div
          className="absolute -inset-8 rounded-[32px] bg-gradient-to-br from-emerald-400/16 via-cyan-400/12 to-amber-300/10 blur-3xl"
          aria-hidden="true"
        />
        <div className="relative rounded-[28px] border border-white/10 bg-white/5 p-8 shadow-2xl backdrop-blur">
          <div className="space-y-6">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-emerald-200/80 mb-2">
                System snapshot
              </p>
              <h3 className="text-xl font-semibold text-[#f8f5ed] mb-3">
                Real-time operations center
              </h3>
            </div>
            <ul className="space-y-3 text-sm text-[#dcd6c8]">
              <li className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-emerald-300 flex-shrink-0" />
                <span>Track contributions, loans, welfare funds, and investments in real-time</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-cyan-300 flex-shrink-0" />
                <span>M-Pesa integration with STK Push, payment verification, and callbacks</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-amber-300 flex-shrink-0" />
                <span>Automated loan approvals, meeting management, and comprehensive reports</span>
              </li>
            </ul>
            <div className="pt-4 border-t border-white/10">
              <Link
                to="/#pricing"
                className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-200 hover:text-emerald-100 transition"
              >
                View pricing plans
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

HeroSection.propTypes = {
  badge: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

HeroSection.defaultProps = {
  badge: null,
  title: null,
  subtitle: null,
};

export default HeroSection;
